import { useCallback, useEffect, useRef, useState } from "react";
import { getFleetPeriods } from "../../../../services/clearing/fleet-dashboard";
import type { FleetPeriod, Granularity } from "../../../../types/chargingStationDashboard";
import { isFleetFailure } from "../../../../types/chargingStationDashboard";
import { parseIsoDate, periodBounds, periodContains, toIsoDate, trendBounds } from "../../../../utils/fleetPeriods";

export interface FleetPeriodsState {
  loading: boolean;
  /** A genuine failure, with the server's message where it gave one. */
  error: string | null;
  /** No day in the range has ever been computed — distinct from a period in which nothing happened. */
  neverComputed: boolean;
  /** The trend, oldest first, ending with the selected period. */
  periods: FleetPeriod[];
  selected: FleetPeriod | null;
  /** The period the selected one is compared against, when the server returned it. */
  previous: FleetPeriod | null;
  reload: () => void;
}

/**
 * BOF-76 — one request for the selected period, its predecessor and the trend behind them.
 *
 * Two things this hook guarantees so the panels do not have to:
 *
 * - **No stale figures.** Every new request clears what was shown, and a response that arrives after a
 *   newer request was made is discarded. Without both, switching months quickly could leave one panel
 *   showing the old month beside panels showing the new one (US1 scenario 4).
 * - **No thrown errors.** The service never throws; a never-computed range is reported as such rather
 *   than as a failure, because it has a remedy and "no data" does not.
 */
export const useFleetPeriods = (granularity: Granularity, anchor: Date): FleetPeriodsState => {
  const [state, setState] = useState<Omit<FleetPeriodsState, "reload">>({
    loading: true,
    error: null,
    neverComputed: false,
    periods: [],
    selected: null,
    previous: null,
  });
  const latestRequest = useRef(0);
  const anchorKey = toIsoDate(anchor);

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setState({ loading: true, error: null, neverComputed: false, periods: [], selected: null, previous: null });

    const anchorDate = parseIsoDate(anchorKey);
    const range = trendBounds(granularity, anchorDate);
    const result = await getFleetPeriods({ granularity, from: range.from, to: range.to, includeIncomplete: true });
    if (requestId !== latestRequest.current) return; // a newer request has superseded this one

    if (isFleetFailure(result)) {
      setState({
        loading: false,
        error: result.neverComputed ? null : result.message,
        neverComputed: result.neverComputed,
        periods: [],
        selected: null,
        previous: null,
      });
      return;
    }

    const periods = result.data.periods ?? [];
    const start = toIsoDate(periodBounds(granularity, anchorDate).from);
    const selected = periods.find((p) => periodContains(p, start)) ?? null;
    const previous = selected ? periods.find((p) => p.key === selected.previousKey) ?? null : null;
    setState({ loading: false, error: null, neverComputed: false, periods, selected, previous });
  }, [granularity, anchorKey]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
};
